<template>
  <fieldset class="checkboxes-fieldset" :disabled="disabled">
    <legend v-if="legend">
      {{ legend }}
    </legend>
    <CheckRadio
      v-for="(option, i) in checkboxLayout()"
      :key="'checkbox-set-' + i"
      :error-message="false"
      type="checkbox"
      :value="option.value"
      :check-override="option.override"
      :override-value="option.overrideValue"
      :name="optionSetName"
      :emit-input-change="'checkbox-selected'"
      :not-valid-override="state.notValid"
      @checkbox-selected="checkboxChanged"
    >
      {{ option.value }}
    </CheckRadio>
    <div v-if="state.notValid" class="checkcheckbox-invalid-container">
      <ErrorWarningFill />
      <p>
        <slot name="checkboxError"> Checkboxes error message. </slot>
      </p>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { ChangePayload } from '../types/solo-input';
import CheckRadio from './CheckRadio.vue';

export interface CheckboxesReturn {
  allChecked: string[];
  latestValue: string;
  name: string;
}

export interface CheckboxOptions {
  value: string;
  override: boolean | undefined;
  overrideValue: boolean | undefined;
}

type CheckboxEmit = 'checkboxChanged' | 'filterChange';

const emit = defineEmits(['checkboxChanged', 'filterChange']);

const props = defineProps({
  emitCheckboxChanged: {
    type: String as () => CheckboxEmit,
    default: undefined,
  },
  checkboxOptions: {
    type: Array as () => string[],
    required: true,
  },
  inverseCheckboxOptions: {
    type: Array as () => CheckboxOptions[],
    default: undefined,
  },
  optionSetName: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  legend: {
    type: String,
    default: undefined,
  },
  notValidOverride: {
    type: Boolean,
    default: false,
  },
});

const state = reactive({
  checkboxesChecked: [] as string[],
  invertedChecked: props.inverseCheckboxOptions as CheckboxOptions[],
  notValid: props.notValidOverride,
});

watch(
  () => props.inverseCheckboxOptions,
  (newValue) => {
    state.invertedChecked = newValue as CheckboxOptions[];
  }
);

onMounted(() => {
  if (state.checkboxesChecked.length === 0 && state.invertedChecked) {
    state.checkboxesChecked = checkboxLayout().reduce((filtered: string[], option: CheckboxOptions) => {
      if (option.overrideValue) {
        filtered.push(option.value);
      }
      return filtered;
    }, [] as string[]);
  }
});

function checkboxChanged(e: ChangePayload) {
  const valueIndex = state.checkboxesChecked.indexOf(e.value);
  if (e.checked && valueIndex === -1) {
    state.checkboxesChecked.push(e.value);
  }
  if (!e.checked && valueIndex !== -1) {
    state.checkboxesChecked.splice(valueIndex, 1);
  }
  if (props.emitCheckboxChanged) {
    const payload = {
      allChecked: state.checkboxesChecked,
      latestValue: e.value,
      name: props.optionSetName,
    };
    emit(props.emitCheckboxChanged, payload);
  }
}
function checkboxLayout(): CheckboxOptions[] {
  const options =
    state.invertedChecked ||
    props.checkboxOptions.map((value: string) => {
      return {
        value,
      };
    });
  return options;
}
</script>

<style lang="scss" scoped>
.checkboxes-fieldset {
  border-radius: 4px;
  box-sizing: border-box;
}

.checkcheckbox-invalid-container {
  align-items: center;
  display: flex;
  margin-top: 0.45rem;
  color: $color-danger-dark;
  fill: $color-danger-dark;

  :deep(svg) {
    height: 1rem;
    margin-right: 0.25rem;
    width: 1rem;
  }

  p {
    margin: 0;
  }
}
</style>
